<template>
  <div v-if="menuItems.length" class="customer-menu">
    <button
      v-for="item in menuItems"
      :key="item.id"
      type="button"
      class="customer-menu__item"
      @click="onItemClick(item)"
    >
      <i :class="getIcon(item)" aria-hidden="true" />
      <span>{{ menuLabel(item.linkLabel) }}</span>
      <span v-if="item.badge" class="customer-menu__badge">
        {{ menuLabel(item.badge) }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import commons from '@/utils/Common'
import {
  fetchCustomerMenu,
  type CustomerMenuItem,
} from '@/utils/customerMenu'

const emit = defineEmits<{
  (e: 'navigate', path: string): void
  (e: 'logout'): void
}>()

const { t, te } = useI18n()

const menuTree = ref<CustomerMenuItem[]>([])
const isLogin = ref(false)

const FALLBACK_ICONS = [
  'fa-solid fa-chart-pie',
  'fa-solid fa-list-ul',
  'fa-regular fa-address-card',
  'fa-solid fa-briefcase',
  'fa-solid fa-user-group',
  'fa-regular fa-credit-card',
]

const refreshLogin = () => {
  isLogin.value = !!commons.checkLogin()
}

const isVisibleItem = (item: CustomerMenuItem) => {
  if (item.slug === 'auth') return isLogin.value
  return !isLogoutItem(item)
}

const isLogoutItem = (item: CustomerMenuItem) => {
  const slug = String(item.slug || '').toLowerCase()
  const label = String(item.linkLabel || '').toLowerCase()
  return (
    slug === 'logout' ||
    slug === '/logout' ||
    slug.endsWith('/logout') ||
    label === 'text.logout' ||
    label.endsWith('.logout')
  )
}

const menuItems = computed(() => {
  if (!menuTree.value.length) return []

  const groupRoot = menuTree.value.find((root) => root.children?.length)
  if (groupRoot?.children?.length) {
    return groupRoot.children.filter(isVisibleItem)
  }

  return menuTree.value.filter(isVisibleItem)
})

const menuLabel = (linkLabel?: string) => {
  const label = String(linkLabel || '').trim()
  if (!label) return ''
  return te(label) ? t(label) : label
}

const getIcon = (item: CustomerMenuItem) => {
  if (item.icon) return item.icon
  const idx = Math.abs(Number(item.id) || 0) % FALLBACK_ICONS.length
  return FALLBACK_ICONS[idx]
}

const onItemClick = (item: CustomerMenuItem) => {
  if (isLogoutItem(item)) {
    emit('logout')
    return
  }
  emit('navigate', item.slug || '/')
}

const loadMenu = async () => {
  menuTree.value = await fetchCustomerMenu()
}

onMounted(async () => {
  refreshLogin()
  await loadMenu()
})
</script>

<style scoped>
.customer-menu {
  padding: 6px 0;
  border-bottom: 1px solid #eee;
}

.customer-menu__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 16px;
  border: none;
  background: transparent;
  color: #222;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.customer-menu__item i {
  width: 18px;
  color: #444;
  font-size: 14px;
  text-align: center;
  flex: 0 0 auto;
}

.customer-menu__item span {
  flex: 1 1 auto;
  min-width: 0;
}

.customer-menu__item:hover {
  background: #f2f2f2;
}

.customer-menu__item:active {
  background: #ebebeb;
}

.customer-menu__badge {
  flex: 0 0 auto !important;
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 4px;
  background: #e0f7fa;
  color: #00838f;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
</style>
